import React, { useRef, useEffect, useState } from "react";
import "./multi.carousal.style.css";

/**
 * InfiniteScrollStrip — a seamless, lag-free infinite horizontal scroll.
 * Works by duplicating the children multiple times so the strip always
 * appears continuous without any reset jump.
 */
function MultiCarousalLogo({ children, speed = 1500, ...props }) {
  const trackRef = useRef(null);
  const [duplicates, setDuplicates] = useState(3);

  // Convert children to array so we can duplicate them
  const childArray = React.Children.toArray(children);

  // Derive animation duration from the `speed` prop.
  // speed prop is used as autoPlaySpeed in ms per slide in the old carousel,
  // so we interpret it proportionally: more items → faster total strip speed.
  // We target ~60px/s as a comfortable reading speed.
  const SPEED_PX_PER_SEC = 60; // pixels per second

  useEffect(() => {
    // After mount, measure the single strip width and ensure we have
    // enough copies to fill at least 3× the container width so there
    // is never a visible gap.
    const track = trackRef.current;
    if (!track) return;

    const resizeObserver = new ResizeObserver(() => {
      const container = track.parentElement;
      if (!container) return;
      const singleStripWidth = track.scrollWidth / duplicates;
      const containerWidth = container.offsetWidth;
      const needed = Math.max(3, Math.ceil((containerWidth * 3) / singleStripWidth) + 1);
      if (needed !== duplicates) setDuplicates(needed);
    });

    resizeObserver.observe(track.parentElement);
    return () => resizeObserver.disconnect();
  }, [duplicates, childArray.length]);

  // Calculate animation duration:  width of ONE copy / speed
  // We'll use a CSS variable updated via inline style once we know the width.
  const itemWidth = 160; // approximate px per item (logo card width)
  const singleSetWidth = childArray.length * itemWidth;
  const duration = singleSetWidth / SPEED_PX_PER_SEC; // seconds

  return (
    <div className="infinite-scroll-wrapper" aria-label={props.section}>
      <div
        ref={trackRef}
        className="infinite-scroll-track"
        style={{ "--duration": `${duration}s`, "--sets": duplicates }}
      >
        {/* Render the set multiple times for seamless looping */}
        {Array.from({ length: duplicates }).map((_, setIdx) =>
          childArray.map((child, childIdx) => (
            <div
              className="infinite-scroll-item"
              key={`set-${setIdx}-item-${childIdx}`}
            >
              {child}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MultiCarousalLogo;
